import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BillingPlan {
  id: string;
  name: string;
  price_brl: number;
  monthly_message_limit: number | null;
  monthly_ai_calls_limit: number | null;
  max_groups: number | null;
  max_members: number | null;
  features: Record<string, boolean>;
  stripe_price_id: string | null;
  active: boolean;
}

export interface BillingSubscription {
  id: string;
  org_id: string;
  plan_id: string;
  status: string;
  cancel_at_period_end: boolean;
  current_period_start: string | null;
  current_period_end: string | null;
  billing_plans?: BillingPlan;
}

export interface BillingInvoice {
  id: string;
  amount_cents: number;
  currency: string;
  status: string;
  invoice_url: string | null;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
}

export interface OrgUsage {
  monthly_message_limit: number | null;
  monthly_ai_calls_limit: number | null;
  plan: string | null;
  messages_used: number;
  ai_calls_used: number;
}

export function useBilling(orgId: string | undefined) {
  const plansQuery = useQuery({
    queryKey: ["billing-plans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("billing_plans")
        .select("*")
        .eq("active", true)
        .order("price_brl", { ascending: true });
      if (error) throw error;
      return data as BillingPlan[];
    },
  });

  const subscriptionQuery = useQuery({
    queryKey: ["billing-subscription", orgId],
    queryFn: async () => {
      if (!orgId) return null;
      const { data, error } = await supabase
        .from("billing_subscriptions")
        .select("*, billing_plans(*)")
        .eq("org_id", orgId)
        .maybeSingle();
      if (error) throw error;
      return data as BillingSubscription | null;
    },
    enabled: !!orgId,
  });

  const invoicesQuery = useQuery({
    queryKey: ["billing-invoices", orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from("billing_invoices")
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data as BillingInvoice[];
    },
    enabled: !!orgId,
  });

  const usageQuery = useQuery({
    queryKey: ["billing-usage", orgId],
    queryFn: async () => {
      if (!orgId) return null;

      const { data: org } = await supabase
        .from("organizations")
        .select("monthly_message_limit, monthly_ai_calls_limit, plan")
        .eq("id", orgId)
        .single();

      const currentPeriod = new Date().toISOString().slice(0, 7); // YYYY-MM
      const { data: usage } = await supabase
        .from("usage_tracking")
        .select("metric, value")
        .eq("org_id", orgId)
        .eq("period_key", currentPeriod);

      const messagesUsed = usage?.find(u => u.metric === "messages")?.value ?? 0;
      const aiCallsUsed = usage?.find(u => u.metric === "ai_calls")?.value ?? 0;

      return {
        monthly_message_limit: org?.monthly_message_limit,
        monthly_ai_calls_limit: org?.monthly_ai_calls_limit,
        plan: org?.plan,
        messages_used: messagesUsed,
        ai_calls_used: aiCallsUsed,
      } as OrgUsage;
    },
    enabled: !!orgId,
  });

  const createCheckout = async (planId: string) => {
    const { data, error } = await supabase.functions.invoke("create-checkout", {
      body: { plan_id: planId, org_id: orgId },
    });
    if (error) throw error;
    if (data?.url) window.location.href = data.url;
    return data;
  };

  const openBillingPortal = async () => {
    const { data, error } = await supabase.functions.invoke("billing-portal", {
      body: { org_id: orgId },
    });
    if (error) throw error;
    if (data?.url) window.location.href = data.url;
    return data;
  };

  return {
    plans: plansQuery.data ?? [],
    subscription: subscriptionQuery.data,
    invoices: invoicesQuery.data ?? [],
    usage: usageQuery.data,
    isLoading: plansQuery.isLoading || subscriptionQuery.isLoading,
    createCheckout,
    openBillingPortal,
  };
}
